import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import navigationImage from '../../assets/driver map.png';
import siteOverviewImage from '../../assets/site_location.png';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { fetchAllOrgChartUsers } from '../../services/orgChartApiService';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { dashboardApi } from '../Dashboard/dashboardApi';
import { driverApi } from '../Driver/driverApi';
import { inventoryApi } from '../Inventory/inventoryApi';
import { getAllSites } from '../SiteManagement/siteApi';
import { taskApi } from '../Tasks/taskApi';
import { Card } from '../ui';
import './Analytics.css';
import { analyticsApi } from './analyticsApi';
import {
  ANALYTICS_CARD_IDS,
  getVisibleAnalyticsCards,
  resolveAnalyticsLayout
} from './analyticsCardPermissions';
// import AnalyticsPermissionTester, {
//   ANALYTICS_CARD_OPTIONS
// } from './AnalyticsPermissionTester';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

const EMPLOYEE_OVERVIEW_COLORS = [
  '#8B5CF6',
  '#0EA5E9',
  '#34D399',
  '#F97316',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#F43F5E'
];

const NO_EMPLOYEE_DATA_SEGMENT = [{ label: 'No data', value: 1, color: '#E5E7EB' }];
const TASKS_DUMMY_METRICS = {
  total: 12,
  ongoing: 8,
  completed: 4
};
const ISSUES_DUMMY_METRICS = {
  total: 20,
  active: 8,
  resolved: 12
};

const EMPLOYEE_ATTENDANCE_WEEK_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const EMPLOYEE_ATTENDANCE_DUMMY_SERIES = {
  totalAssigned: [120, 125, 123, 130, 128, 132, 134],
  onDuty: [95, 100, 98, 105, 101, 108, 110],
  offDuty: [25, 25, 25, 25, 27, 24, 24]
};

const SMART_NAVIGATION_WEEK_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const SMART_NAVIGATION_DUMMY_SERIES = {
  assigned: [68, 70, 72, 73, 75, 76, 78],
  completed: [40, 42, 44, 46, 45, 47, 49],
  pending: [10, 9, 11, 12, 10, 9, 8]
};

const toTitleCase = value =>
  value
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const normalizeEmployeeRoleLabel = rawLabel => {
  if (!rawLabel || typeof rawLabel !== 'string') {
    return 'Unknown';
  }

  const cleaned = rawLabel.replace(/_/g, ' ').trim();
  if (!cleaned) {
    return 'Unknown';
  }

  const normalized = toTitleCase(cleaned);
  
  // Replace "Guard" with "Employee"
  if (normalized.toLowerCase() === 'guard') {
    return 'Employee';
  }

  return normalized;
};

const extractUsersFromResponse = response => {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.users)) {
    return response.users;
  }

  return [];
};

const mapRoleCountsToSegments = roleCounts =>
  Object.entries(roleCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], index) => ({
      label,
      value,
      color: EMPLOYEE_OVERVIEW_COLORS[index % EMPLOYEE_OVERVIEW_COLORS.length]
    }));

const ANALYTICS_CACHE_KEY = 'analyticsDashboardCache';
const ANALYTICS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const AnalyticsDashboard = () => {
  // Log screen view
  useScreenLogging('AnalyticsDashboard', {
    component: 'AnalyticsDashboard',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [sitesCount, setSitesCount] = useState(0);
  const [totalAssignedEmployees, setTotalAssignedEmployees] = useState(0);
  const [onDutyEmployees, setOnDutyEmployees] = useState(0);
  const [offDutyEmployees, setOffDutyEmployees] = useState(0);
  const [upcomingRides, setUpcomingRides] = useState(0);
  const [ongoingRides, setOngoingRides] = useState(0);
  const [notStartedRides, setNotStartedRides] = useState(0);
  const [totalInventoryItems, setTotalInventoryItems] = useState(0);
  const [goodConditionItems, setGoodConditionItems] = useState(0);
  const [underMaintenanceItems, setUnderMaintenanceItems] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [ongoingTasks, setOngoingTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [totalIssues, setTotalIssues] = useState(0);
  const [activeIssues, setActiveIssues] = useState(0);
  const [resolvedIssues, setResolvedIssues] = useState(0);
  const [employeeOverviewSegments, setEmployeeOverviewSegments] = useState([]);
  const [isEmployeeOverviewLoading, setIsEmployeeOverviewLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceWeekData, setAttendanceWeekData] = useState({
    labels: EMPLOYEE_ATTENDANCE_WEEK_LABELS,
    totalAssigned: [],
    onDuty: [],
    offDuty: []
  });
  const [isAttendanceWeekLoading, setIsAttendanceWeekLoading] = useState(true);
  const [driverRideWeekData, setDriverRideWeekData] = useState({
    labels: SMART_NAVIGATION_WEEK_LABELS,
    assigned: [],
    completed: [],
    undone: []
  });
  const [isDriverRideWeekLoading, setIsDriverRideWeekLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  // const [isTestPanelOpen, setIsTestPanelOpen] = useState(false);
  // const [isTestMode, setIsTestMode] = useState(false);
  // const [testVisibleCards, setTestVisibleCards] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      try {
        const permissions = await userService.getUserPermissions();
        if (!cancelled) {
          setUserPermissions(Array.isArray(permissions) ? permissions : []);
        }
      } catch (permError) {
        console.error('Error loading analytics permissions:', permError);
        if (!cancelled) {
          setUserPermissions([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPermissions(false);
        }
      }
    };

    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  const rbacVisibleCards = useMemo(
    () => getVisibleAnalyticsCards(userPermissions),
    [userPermissions]
  );

  // useEffect(() => {
  //   if (!isLoadingPermissions) {
  //     setTestVisibleCards(getVisibleAnalyticsCards(userPermissions));
  //   }
  // }, [userPermissions, isLoadingPermissions]);

  // const visibleCards = useMemo(
  //   () => (isTestMode ? testVisibleCards : rbacVisibleCards),
  //   [isTestMode, testVisibleCards, rbacVisibleCards]
  // );
  const visibleCards = rbacVisibleCards;

  const layout = useMemo(() => resolveAnalyticsLayout(visibleCards), [visibleCards]);

  // const handleSelectAllTestCards = useCallback(() => {
  //   setIsTestMode(true);
  //   setTestVisibleCards(
  //     ANALYTICS_CARD_OPTIONS.reduce((acc, option) => {
  //       acc[option.id] = true;
  //       return acc;
  //     }, {})
  //   );
  // }, []);

  // const handleClearAllTestCards = useCallback(() => {
  //   setIsTestMode(true);
  //   setTestVisibleCards(
  //     ANALYTICS_CARD_OPTIONS.reduce((acc, option) => {
  //       acc[option.id] = false;
  //       return acc;
  //     }, {})
  //   );
  // }, []);

  // const handleResetTestCardsToRole = useCallback(() => {
  //   setIsTestMode(false);
  //   setTestVisibleCards({ ...rbacVisibleCards });
  // }, [rbacVisibleCards]);

  // const handleOpenTestPanel = useCallback(
  //   open => {
  //     setIsTestPanelOpen(open);
  //     if (open) {
  //       setIsTestMode(true);
  //       setTestVisibleCards(prev => {
  //         const hasKeys = Object.keys(prev).length > 0;
  //         return hasKeys ? prev : { ...rbacVisibleCards };
  //       });
  //     }
  //   },
  //   [rbacVisibleCards]
  // );

  // const handleToggleTestCard = useCallback(cardId => {
  //   setIsTestMode(true);
  //   setTestVisibleCards(prev => ({
  //     ...prev,
  //     [cardId]: !prev[cardId]
  //   }));
  // }, []);

  const canViewSite = visibleCards[ANALYTICS_CARD_IDS.SITE];
  const canViewEmployeeOverview = visibleCards[ANALYTICS_CARD_IDS.EMPLOYEE_OVERVIEW];
  const canViewEmployeeAttendance = visibleCards[ANALYTICS_CARD_IDS.EMPLOYEE_ATTENDANCE];
  const canViewRides = visibleCards[ANALYTICS_CARD_IDS.RIDES];
  const canViewInventory = visibleCards[ANALYTICS_CARD_IDS.INVENTORY];
  const canViewTasksIssues = visibleCards[ANALYTICS_CARD_IDS.TASKS_ISSUES];

  const employeeOverviewChartData = useMemo(() => {
    const segments = employeeOverviewSegments.length > 0 ? employeeOverviewSegments : NO_EMPLOYEE_DATA_SEGMENT;

    return {
      labels: segments.map(segment => segment.label),
      datasets: [
        {
          data: segments.map(segment => segment.value),
          backgroundColor: segments.map(segment => segment.color),
          borderWidth: 0,
          hoverOffset: 6
        }
      ]
    };
  }, [employeeOverviewSegments]);

  const employeeOverviewTotal = useMemo(
    () => employeeOverviewSegments.reduce((sum, segment) => sum + segment.value, 0),
    [employeeOverviewSegments]
  );

  const employeeOverviewChartOptions = useMemo(() => ({
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: context => {
            if (employeeOverviewSegments.length === 0) {
              return 'No employee data available';
            }
            const value = context.parsed ?? 0;
            const percentage = employeeOverviewTotal ? ((value / employeeOverviewTotal) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false,
    responsive: true
  }), [employeeOverviewSegments, employeeOverviewTotal]);

  const employeeAttendanceDonutData = useMemo(() => {
    const hasNoData = totalAssignedEmployees === 0 && onDutyEmployees === 0 && offDutyEmployees === 0;

    if (hasNoData) {
      return {
        datasets: [
          {
            data: [100, 0],
            backgroundColor: ['#E5E7EB', '#F3F4F6'],
            borderWidth: 0,
            cutout: '68%',
            radius: '100%'
          },
          {
            data: [100, 0],
            backgroundColor: ['#D1D5DB', '#F3F4F6'],
            borderWidth: 0,
            cutout: '50%',
            radius: '80%'
          },
          {
            data: [100, 0],
            backgroundColor: ['#9CA3AF', '#F3F4F6'],
            borderWidth: 0,
            cutout: '45%',
            radius: '60%'
          }
        ]
      };
    }

    const totalPercentage = 100;
    const remainingForTotal = Math.max(0.01, 100 - totalPercentage);

    const onDutyPercentage =
      totalAssignedEmployees > 0 ? (onDutyEmployees / totalAssignedEmployees) * 100 : 0;
    const remainingForOnDuty = Math.max(0.01, 100 - onDutyPercentage);

    const offDutyPercentage =
      totalAssignedEmployees > 0 ? (offDutyEmployees / totalAssignedEmployees) * 100 : 0;
    const remainingForOffDuty = Math.max(0.01, 100 - offDutyPercentage);

    return {
      datasets: [
        {
          data: [totalPercentage, remainingForTotal],
          backgroundColor: ['#3B82F6', '#E5E7EB'],
          borderWidth: 0,
          cutout: '68%',
          radius: '100%'
        },
        {
          data: [onDutyPercentage, remainingForOnDuty],
          backgroundColor: ['#22C55E', '#E9F9EF'],
          borderWidth: 0,
          cutout: '50%',
          radius: '80%'
        },
        {
          data: [offDutyPercentage, remainingForOffDuty],
          backgroundColor: ['#EF4444', '#FEE2E2'],
          borderWidth: 0,
          cutout: '45%',
          radius: '60%'
        }
      ]
    };
  }, [totalAssignedEmployees, onDutyEmployees, offDutyEmployees]);

  const employeeAttendanceDonutOptions = useMemo(() => {
    const hasNoData = totalAssignedEmployees === 0 && onDutyEmployees === 0 && offDutyEmployees === 0;

    return {
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !hasNoData,
          callbacks: {
            label: context => {
              if (!context?.parsed) return '';
              const datasetIndex = context.datasetIndex;
              const dataIndex = context.dataIndex;
              const value = Array.isArray(context.parsed) ? context.parsed[0] : context.parsed;

              if (dataIndex !== 0) return '';

              let label = '';
              let percentage = parseFloat(value).toFixed(1);

              if (datasetIndex === 0) {
                label = 'Total Assigned employees';
                percentage = '100.0';
              } else if (datasetIndex === 1) {
                label = 'Employees On Duty';
              } else if (datasetIndex === 2) {
                label = 'Employees Off Duty';
              }

              return `${label}: ${percentage}%`;
            }
          }
        }
      },
      maintainAspectRatio: false,
      responsive: true
    };
  }, [totalAssignedEmployees, onDutyEmployees, offDutyEmployees]);

  const employeeAttendanceChartData = useMemo(() => {
    // Use real data if available, otherwise fallback to dummy data
    const labels = attendanceWeekData.labels.length > 0 
      ? attendanceWeekData.labels 
      : EMPLOYEE_ATTENDANCE_WEEK_LABELS;
    const totalAssigned = attendanceWeekData.totalAssigned.length > 0
      ? attendanceWeekData.totalAssigned
      : EMPLOYEE_ATTENDANCE_DUMMY_SERIES.totalAssigned;
    const onDuty = attendanceWeekData.onDuty.length > 0
      ? attendanceWeekData.onDuty
      : EMPLOYEE_ATTENDANCE_DUMMY_SERIES.onDuty;
    const offDuty = attendanceWeekData.offDuty.length > 0
      ? attendanceWeekData.offDuty
      : EMPLOYEE_ATTENDANCE_DUMMY_SERIES.offDuty;

    return {
      labels,
      datasets: [
        {
          label: 'Assigned employees',
          data: totalAssigned,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        },
        {
          label: 'Employees On Duty',
          data: onDuty,
          borderColor: '#22C55E',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        },
        {
          label: 'Employees Off Duty',
          data: offDuty,
          borderColor: '#F97316',
          backgroundColor: 'rgba(249, 115, 22, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        }
      ]
    };
  }, [attendanceWeekData]);

  const employeeAttendanceChartOptions = useMemo(() => ({
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.parsed?.y ?? context.parsed ?? 0;
            return `${label}: ${value} employees`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#475467', font: { weight: '600' } },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        title: {
          display: true,
          text: 'Weekdays',
          color: '#475467',
          font: { weight: '600' }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#475467'
        },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        title: {
          display: true,
          text: 'Employee count',
          color: '#475467',
          font: { weight: '600' }
        }
      }
    }
  }), []);

  const smartNavigationChartData = useMemo(() => {
    // Use real data if available, otherwise fallback to dummy data
    const labels = driverRideWeekData.labels.length > 0 
      ? driverRideWeekData.labels 
      : SMART_NAVIGATION_WEEK_LABELS;
    const assigned = driverRideWeekData.assigned.length > 0
      ? driverRideWeekData.assigned
      : SMART_NAVIGATION_DUMMY_SERIES.assigned;
    const completed = driverRideWeekData.completed.length > 0
      ? driverRideWeekData.completed
      : SMART_NAVIGATION_DUMMY_SERIES.completed;
    const undone = driverRideWeekData.undone.length > 0
      ? driverRideWeekData.undone
      : SMART_NAVIGATION_DUMMY_SERIES.pending;

    return {
      labels,
      datasets: [
        {
          label: 'Assigned Rides',
          data: assigned,
          borderColor: '#1D4ED8',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        },
        {
          label: 'Completed Rides',
          data: completed,
          borderColor: '#22C55E',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        },
        {
          label: 'Pending Rides',
          data: undone,
          borderColor: '#F97316',
          backgroundColor: 'rgba(249, 115, 22, 0.15)',
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: true
        }
      ]
    };
  }, [driverRideWeekData]);

  const smartNavigationChartOptions = useMemo(() => ({
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.parsed?.y ?? context.parsed ?? 0;
            return `${label}: ${value} rides`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#475467', font: { weight: '600' } },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        title: {
          display: true,
          text: 'Weekdays',
          color: '#475467',
          font: { weight: '600' }
        }
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#475467' },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        title: {
          display: true,
          text: 'Ride count',
          color: '#475467',
          font: { weight: '600' }
        }
      }
    }
  }), []);

  const rideStatusStackedBarData = useMemo(() => {
    const total = ongoingRides + upcomingRides + notStartedRides;
    const hasNoData = total === 0;

    if (hasNoData) {
      return {
        labels: ['Rides'],
        datasets: [
          {
            label: 'No data',
            data: [100],
            backgroundColor: '#E5E7EB',
            borderRadius: 8
          }
        ]
      };
    }

    // Calculate percentages for 100% stacked bar
    const ongoingPercentage = total > 0 ? (ongoingRides / total) * 100 : 0;
    const upcomingPercentage = total > 0 ? (upcomingRides / total) * 100 : 0;
    const pendingPercentage = total > 0 ? (notStartedRides / total) * 100 : 0;

    return {
      labels: ['Rides'],
      datasets: [
        {
          label: 'On going Rides',
          data: [ongoingPercentage],
          backgroundColor: '#1D4ED8',
          borderRadius: 8
        },
        {
          label: 'Upcoming rides',
          data: [upcomingPercentage],
          backgroundColor: '#22C55E',
          borderRadius: 8
        },
        {
          label: 'Pending Rides',
          data: [pendingPercentage],
          backgroundColor: '#F97316',
          borderRadius: 8
        }
      ]
    };
  }, [ongoingRides, upcomingRides, notStartedRides]);

  const rideStatusStackedBarOptions = useMemo(() => {
    const total = ongoingRides + upcomingRides + notStartedRides;
    
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            pointStyle: 'rectRounded',
            padding: 12
          }
        },
        tooltip: {
          callbacks: {
            label: context => {
              const label = context.dataset.label || '';
              const percentage = context.raw ?? 0;
              
              // Calculate actual count from percentage
              let actualCount = 0;
              if (label === 'On going Rides') {
                actualCount = ongoingRides;
              } else if (label === 'Upcoming rides') {
                actualCount = upcomingRides;
              } else if (label === 'Pending Rides') {
                actualCount = notStartedRides;
              }
              
              return `${label}: ${actualCount} (${percentage.toFixed(1)}%)`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
          ticks: { 
            display: false
          },
          title: {
            display: false
          }
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { display: false }
        }
      }
    };
  }, [ongoingRides, upcomingRides, notStartedRides]);

  const tasksMetrics = useMemo(() => {
    // Always use real API data, even if values are 0
    return {
      total: totalTasks,
      ongoing: ongoingTasks,
      completed: completedTasks
    };
  }, [totalTasks, ongoingTasks, completedTasks]);

  const issuesMetrics = useMemo(() => {
    // Always use real API data, even if values are 0
    return {
      total: totalIssues,
      active: activeIssues,
      resolved: resolvedIssues
    };
  }, [totalIssues, activeIssues, resolvedIssues]);

  const tasksVsIssuesChartData = useMemo(() => ({
    labels: ['Total Assigned', 'In Progress / Active', 'Completed / Resolved'],
    datasets: [
      {
        label: 'Tasks',
        data: [tasksMetrics.total, tasksMetrics.ongoing, tasksMetrics.completed],
        backgroundColor: '#22C55E',
        borderRadius: 12,
        barThickness: 18
      },
      {
        label: 'Issues',
        data: [issuesMetrics.total, issuesMetrics.active, issuesMetrics.resolved],
        backgroundColor: '#FB923C',
        borderRadius: 12,
        barThickness: 18
      }
    ]
  }), [tasksMetrics, issuesMetrics]);

  const tasksVsIssuesChartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, pointStyle: 'rectRounded' }
      },
      tooltip: {
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.raw ?? 0;
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        ticks: { color: '#475467' }
      },
      y: {
        grid: { display: false },
        ticks: { color: '#111827', font: { weight: '600' } }
      }
    }
  }), []);

  const tasksVsIssuesSummaryRows = useMemo(() => [
    {
      label: 'Total Assigned',
      taskValue: tasksMetrics.total,
      issueValue: issuesMetrics.total
    },
    {
      label: 'Ongoing vs Active',
      taskValue: tasksMetrics.ongoing,
      issueValue: issuesMetrics.active
    },
    {
      label: 'Completed vs Resolved',
      taskValue: tasksMetrics.completed,
      issueValue: issuesMetrics.resolved
    }
  ], [tasksMetrics, issuesMetrics]);

  const { segments: inventoryStackedBarSegments, hasData: hasInventoryData } = useMemo(() => {
    const safeGood = Math.max(goodConditionItems, 0);
    const safeMaintenance = Math.max(underMaintenanceItems, 0);
    const safeTotal = Math.max(totalInventoryItems, 0);

    if (safeTotal === 0 && safeGood === 0 && safeMaintenance === 0) {
      return {
        hasData: false,
        segments: [
          {
            key: 'placeholder',
            label: 'No inventory data',
            value: 0,
            percentage: 100,
            color: '#E5E7EB',
            isPlaceholder: true
          }
        ]
      };
    }

    const remainder = Math.max(safeTotal - (safeGood + safeMaintenance), 0);
    const rawSegments = [
      {
        key: 'good',
        label: 'Items In Good Condition',
        value: safeGood,
        color: '#22C55E'
      },
      {
        key: 'maintenance',
        label: 'Items Under Maintenance',
        value: safeMaintenance,
        color: '#F97316'
      }
    ];

    if (remainder > 0) {
      rawSegments.push({
        key: 'other',
        label: 'Other Status',
        value: remainder,
        color: '#0EA5E9'
      });
    }

    const denominator = rawSegments.reduce((sum, segment) => sum + segment.value, 0) || 1;
    const normalizedSegments = rawSegments.map(segment => ({
      ...segment,
      percentage: segment.value === 0 ? 0 : (segment.value / denominator) * 100
    }));

    return {
      hasData: true,
      segments: normalizedSegments
    };
  }, [totalInventoryItems, goodConditionItems, underMaintenanceItems]);

  const inventoryPieSegments = useMemo(() => {
    if (!hasInventoryData) {
      return inventoryStackedBarSegments.map(segment => ({
        ...segment,
        value: segment.isPlaceholder ? 1 : segment.value,
        color: '#E5E7EB'
      }));
    }
    return inventoryStackedBarSegments.filter(segment => !segment.isPlaceholder);
  }, [hasInventoryData, inventoryStackedBarSegments]);

  const inventoryPieChartData = useMemo(
    () => ({
      labels: inventoryPieSegments.map(segment => segment.label),
      datasets: [
        {
          data: inventoryPieSegments.map(segment => segment.value),
          backgroundColor: inventoryPieSegments.map(segment => segment.color),
          borderWidth: 0
        }
      ]
    }),
    [inventoryPieSegments]
  );

  const inventoryPieChartOptions = useMemo(
    () => ({
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: context => {
              const label = context.label || '';
              const value = context.parsed ?? 0;
              return `${label}: ${value}`;
            }
          }
        }
      },
      maintainAspectRatio: false,
      responsive: true
    }),
    []
  );

  const fetchEmployeeOverviewData = useCallback(async () => {
    setIsEmployeeOverviewLoading(true);

    try {
      const usersResponse = await fetchAllOrgChartUsers();
      const users = extractUsersFromResponse(usersResponse);

      if (!users.length) {
        setEmployeeOverviewSegments([]);
        return;
      }

      const roleCounts = users.reduce((acc, user) => {
        const rawRole = user?.spec_role || user?.role_name;
        if (!rawRole) {
          return acc;
        }

        const roleLabel = normalizeEmployeeRoleLabel(rawRole);
        acc[roleLabel] = (acc[roleLabel] || 0) + 1;
        return acc;
      }, {});

      const segments = mapRoleCountsToSegments(roleCounts);
      setEmployeeOverviewSegments(segments);
    } catch (employeeError) {
      console.error('Error fetching employee overview data:', employeeError);
      setEmployeeOverviewSegments([]);
    } finally {
      setIsEmployeeOverviewLoading(false);
    }
  }, []);

  // Function to get the last 7 days including today
  const getLast7Days = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    
    return dates;
  };

  // Function to get weekday abbreviation from date string (YYYY-MM-DD)
  const getWeekdayAbbr = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return weekdays[date.getDay()];
  };

  // Function to fetch attendance data for the last 7 days
  const fetchAttendanceWeekData = useCallback(async () => {
    setIsAttendanceWeekLoading(true);
    
    try {
      const last7Days = getLast7Days();
      const startDate = last7Days[0];
      const endDate = last7Days[last7Days.length - 1];
      
      console.log('Fetching attendance data for week:', { startDate, endDate });
      
      const response = await analyticsApi.getDailyAttendanceStats({
        start_date: startDate,
        end_date: endDate
      });
      
      console.log('Attendance week API response:', response);
      
      // Create a map of date to stats for easy lookup
      const statsMap = new Map();
      if (response && response.stats && Array.isArray(response.stats)) {
        response.stats.forEach(stat => {
          statsMap.set(stat.date, stat);
        });
      }
      
      // Build arrays for each day of the week
      const totalAssigned = [];
      const onDuty = [];
      const offDuty = [];
      const labels = [];
      
      last7Days.forEach(date => {
        const stat = statsMap.get(date);
        const weekday = getWeekdayAbbr(date);
        
        labels.push(weekday);
        totalAssigned.push(stat?.total_assigned_users || 0);
        onDuty.push(stat?.total_present_users || 0);
        // Off duty = assigned - present
        const assigned = stat?.total_assigned_users || 0;
        const present = stat?.total_present_users || 0;
        offDuty.push(Math.max(0, assigned - present));
      });
      
      setAttendanceWeekData({
        labels,
        totalAssigned,
        onDuty,
        offDuty
      });
      
      console.log('Processed attendance week data:', {
        labels,
        totalAssigned,
        onDuty,
        offDuty
      });
      
    } catch (error) {
      console.error('Error fetching attendance week data:', error);
      // Fallback to empty data
      setAttendanceWeekData({
        labels: EMPLOYEE_ATTENDANCE_WEEK_LABELS,
        totalAssigned: [0, 0, 0, 0, 0, 0, 0],
        onDuty: [0, 0, 0, 0, 0, 0, 0],
        offDuty: [0, 0, 0, 0, 0, 0, 0]
      });
    } finally {
      setIsAttendanceWeekLoading(false);
    }
  }, []);

  // Function to fetch driver ride stats for the last 7 days
  const fetchDriverRideWeekData = useCallback(async () => {
    setIsDriverRideWeekLoading(true);
    
    try {
      const last7Days = getLast7Days();
      const startDate = last7Days[0];
      const endDate = last7Days[last7Days.length - 1];
      
      console.log('Fetching driver ride stats for week:', { startDate, endDate });
      
      const response = await analyticsApi.getDailyDriverRideStats({
        start_date: startDate,
        end_date: endDate
      });
      
      console.log('Driver ride week API response:', response);
      
      // Create a map of date to stats for easy lookup
      const statsMap = new Map();
      if (response && response.stats && Array.isArray(response.stats)) {
        response.stats.forEach(stat => {
          statsMap.set(stat.date, stat);
        });
      }
      
      // Build arrays for each day of the week
      const assigned = [];
      const completed = [];
      const undone = [];
      const labels = [];
      
      last7Days.forEach(date => {
        const stat = statsMap.get(date);
        const weekday = getWeekdayAbbr(date);
        
        labels.push(weekday);
        assigned.push(stat?.total_assigned_rides || 0);
        completed.push(stat?.total_completed_rides || 0);
        undone.push(stat?.total_undone_rides || 0);
      });
      
      setDriverRideWeekData({
        labels,
        assigned,
        completed,
        undone
      });
      
      console.log('Processed driver ride week data:', {
        labels,
        assigned,
        completed,
        undone
      });
      
    } catch (error) {
      console.error('Error fetching driver ride week data:', error);
      // Fallback to empty data
      setDriverRideWeekData({
        labels: SMART_NAVIGATION_WEEK_LABELS,
        assigned: [0, 0, 0, 0, 0, 0, 0],
        completed: [0, 0, 0, 0, 0, 0, 0],
        undone: [0, 0, 0, 0, 0, 0, 0]
      });
    } finally {
      setIsDriverRideWeekLoading(false);
    }
  }, []);

  // Ride Status Service Functions
  const getDriverIdFromRide = (ride) => {
    const convoyDetails = ride.convoy_details;
    if (convoyDetails && Array.isArray(convoyDetails) && convoyDetails.length > 0) {
      const firstConvoy = convoyDetails[0];
      return firstConvoy.driver?.id?.toString();
    }
    return null;
  };

  const determineRideStatus = (ride, driverStatus) => {
    const currentTime = new Date();
    const rideStartTime = new Date(ride.ride_start_timestamp);
    const rideEndTime = new Date(ride.ride_end_timestamp);
    
    const status = driverStatus?.status?.toString() || "Unknown";
    const attendanceDetails = driverStatus?.attendance_details;
    
    // Time-based checks
    if (currentTime < rideStartTime) {
      return "UPCOMING"; // Ride scheduled for future
    }
    
    if (currentTime > rideEndTime) {
      return "COMPLETED"; // Ride time has passed
    }
    
    // Status-based checks (within ride time window)
    switch (status) {
      case "Duty not started":
        return "NOT_STARTED"; // Driver hasn't started duty
        
      case "On duty":
        if (attendanceDetails?.start_attendance === true) {
          if (attendanceDetails?.end_attendance === true) {
            return "COMPLETED"; // Both start and end attendance marked
          } else {
            return "ONGOING"; // Start attendance marked, ride in progress
          }
        } else {
          return "NOT_STARTED"; // On duty but no start attendance
        }
        
      case "Duty ended":
        return "COMPLETED"; // Duty has ended
        
      default:
        return "UNKNOWN";
    }
  };

  const getRideStatuses = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      console.log('Fetching rides for today:', today);
      
      const rides = await driverApi.fetchDriverSchedulesByDate(today);
      console.log('Fetched rides:', rides);
      
      let upcomingCount = 0;
      let ongoingCount = 0;
      let notStartedCount = 0;
      
      const currentTime = new Date();
      
      for (const ride of rides) {
        const rideStartTimestamp = ride.ride_start_timestamp;
        const rideEndTimestamp = ride.ride_end_timestamp;
        
        if (rideStartTimestamp && rideEndTimestamp) {
          try {
            const rideStartTime = new Date(rideStartTimestamp);
            const rideEndTime = new Date(rideEndTimestamp);
            
            // Get driver ID from convoy details
            const driverId = getDriverIdFromRide(ride);
            
            if (driverId) {
              // Get driver status for this ride
              const driverStatus = await driverApi.getDriverStatus(ride.ride_id, driverId);
              console.log(`Driver status for ride ${ride.ride_id}:`, driverStatus);
              
              const status = determineRideStatus(ride, driverStatus);
              
              // Categorize rides based on status
              switch (status) {
                case "UPCOMING":
                  upcomingCount++;
                  break;
                case "ONGOING":
                  ongoingCount++;
                  break;
                case "NOT_STARTED":
                  notStartedCount++;
                  break;
                default:
                  console.log(`Unknown status for ride ${ride.ride_id}: ${status}`);
              }
            } else {
              console.warn(`No driver ID found for ride ${ride.ride_id}`);
              // If no driver ID, categorize based on time only
              if (currentTime < rideStartTime) {
                upcomingCount++;
              } else if (currentTime >= rideStartTime && currentTime <= rideEndTime) {
                notStartedCount++; // Assume not started if no driver status
              }
            }
            
          } catch (error) {
            console.error(`Error processing ride ${ride.ride_id}:`, error);
          }
        }
      }
      
      return {
        upcoming: upcomingCount,
        ongoing: ongoingCount,
        notStarted: notStartedCount
      };
      
    } catch (error) {
      console.error('Error fetching ride statuses:', error);
      throw error;
    }
  };

  // Inventory data fetching function
  const fetchInventoryData = async () => {
    try {
      console.log('Fetching inventory data...');
      
      // Fetch all inventory items
      const inventoryResponse = await inventoryApi.getAllInventoryItems();
      console.log('Inventory API response:', inventoryResponse);
      
      if (inventoryResponse.success && inventoryResponse.data) {
        const inventoryItems = inventoryResponse.data.items || inventoryResponse.data || [];
        
        // Count total items
        const totalItems = inventoryItems.length;
        setTotalInventoryItems(totalItems);
        
        // Count items by condition
        let goodConditionCount = 0;
        let underMaintenanceCount = 0;
        
        inventoryItems.forEach(item => {
          const condition = item.condition || item.status;
          if (condition === 'good_condition') {
            goodConditionCount++;
          } else if (condition === 'under_maintenance') {
            underMaintenanceCount++;
          }
        });
        
        setGoodConditionItems(goodConditionCount);
        setUnderMaintenanceItems(underMaintenanceCount);
        
        console.log('Inventory counts:', {
          total: totalItems,
          goodCondition: goodConditionCount,
          underMaintenance: underMaintenanceCount
        });
        
        return {
          total: totalItems,
          goodCondition: goodConditionCount,
          underMaintenance: underMaintenanceCount
        };
      } else {
        console.warn('Inventory API returned unsuccessful response:', inventoryResponse);
        return {
          total: 0,
          goodCondition: 0,
          underMaintenance: 0
        };
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      return {
        total: 0,
        goodCondition: 0,
        underMaintenance: 0
      };
    }
  };

  // Helper function to filter items by current month using modifiedTimestamp
  const filterByCurrentMonth = (items) => {
    if (!Array.isArray(items)) {
      return [];
    }
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based month (0 = January, 11 = December)
    
    console.log(`Filtering items for current month: ${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}`);
    console.log(`Total items to filter: ${items.length}`);
    
    const filteredItems = items.filter(item => {
      if (!item.modifiedTimestamp) {
        return false; // Skip items without modifiedTimestamp
      }
      
      try {
        const itemDate = new Date(item.modifiedTimestamp);
        const itemYear = itemDate.getFullYear();
        const itemMonth = itemDate.getMonth();
        
        // Check if the item is from the current month and year
        const isCurrentMonth = itemYear === currentYear && itemMonth === currentMonth;
        
        if (isCurrentMonth) {
          console.log(`Item ${item.id || 'unknown'} is from current month: ${item.modifiedTimestamp}`);
        }
        
        return isCurrentMonth;
      } catch (error) {
        console.warn('Error parsing modifiedTimestamp:', item.modifiedTimestamp, error);
        return false;
      }
    });
    
    console.log(`Filtered items count for current month: ${filteredItems.length}`);
    return filteredItems;
  };

  // Task data fetching function
  const fetchTaskData = async () => {
    try {
      console.log('Fetching task data...');
      
      // Fetch all three types of task data in parallel
      const [endedTasksResponse, notEndedTasksResponse, todayTasksResponse] = await Promise.all([
        taskApi.getAllEndedTasksAndIssues(),
        taskApi.getAllNotEndedTasksAndIssues(),
        taskApi.getTasks() // Get today's tasks
      ]);
      
      console.log('Ended tasks and issues response:', endedTasksResponse);
      console.log('Not ended tasks and issues response:', notEndedTasksResponse);
      console.log('Today tasks response:', todayTasksResponse);
      
      // Extract tasks from the combined response
      let endedTasks = [];
      let notEndedTasks = [];
      
      // Process ended tasks and issues response
      if (endedTasksResponse && endedTasksResponse.data && Array.isArray(endedTasksResponse.data)) {
        endedTasks = taskApi.filterTasks(endedTasksResponse.data);
      } else if (endedTasksResponse && Array.isArray(endedTasksResponse)) {
        endedTasks = taskApi.filterTasks(endedTasksResponse);
      } else if (endedTasksResponse && endedTasksResponse.tasks && Array.isArray(endedTasksResponse.tasks)) {
        endedTasks = taskApi.filterTasks(endedTasksResponse.tasks);
      }
      
      // Process not ended tasks and issues response
      if (notEndedTasksResponse && notEndedTasksResponse.data && Array.isArray(notEndedTasksResponse.data)) {
        notEndedTasks = taskApi.filterTasks(notEndedTasksResponse.data);
      } else if (notEndedTasksResponse && Array.isArray(notEndedTasksResponse)) {
        notEndedTasks = taskApi.filterTasks(notEndedTasksResponse);
      } else if (notEndedTasksResponse && notEndedTasksResponse.tasks && Array.isArray(notEndedTasksResponse.tasks)) {
        notEndedTasks = taskApi.filterTasks(notEndedTasksResponse.tasks);
      }
      
      // Filter tasks by current month
      const currentMonthEndedTasks = filterByCurrentMonth(endedTasks);
      const currentMonthNotEndedTasks = filterByCurrentMonth(notEndedTasks);
      
      // Calculate task counts for current month
      const completedCount = currentMonthEndedTasks.length;
      const ongoingCount = currentMonthNotEndedTasks.length;
      let totalCount = completedCount + ongoingCount;
      
      // If today's tasks API provides a different total, use that instead
      if (todayTasksResponse && todayTasksResponse.data && Array.isArray(todayTasksResponse.data)) {
        const todayTasks = taskApi.filterTasks(todayTasksResponse.data);
        const currentMonthTodayTasks = filterByCurrentMonth(todayTasks);
        totalCount = currentMonthTodayTasks.length;
      } else if (todayTasksResponse && Array.isArray(todayTasksResponse)) {
        const todayTasks = taskApi.filterTasks(todayTasksResponse);
        const currentMonthTodayTasks = filterByCurrentMonth(todayTasks);
        totalCount = currentMonthTodayTasks.length;
      } else if (todayTasksResponse && todayTasksResponse.tasks && Array.isArray(todayTasksResponse.tasks)) {
        const todayTasks = taskApi.filterTasks(todayTasksResponse.tasks);
        const currentMonthTodayTasks = filterByCurrentMonth(todayTasks);
        totalCount = currentMonthTodayTasks.length;
      }
      
      setTotalTasks(totalCount);
      setOngoingTasks(ongoingCount);
      setCompletedTasks(completedCount);
      
      console.log('Task counts for current month:', {
        total: totalCount,
        ongoing: ongoingCount,
        completed: completedCount
      });
      
      return {
        total: totalCount,
        ongoing: ongoingCount,
        completed: completedCount
      };
      
    } catch (error) {
      console.error('Error fetching task data:', error);
      return {
        total: 0,
        ongoing: 0,
        completed: 0
      };
    }
  };

  // Issues data fetching function
  const fetchIssuesData = async () => {
    try {
      console.log('Fetching issues data...');
      
      // Fetch all issues data in parallel
      const [endedIssuesResponse, notEndedIssuesResponse] = await Promise.all([
        taskApi.getAllEndedTasksAndIssues(),
        taskApi.getAllNotEndedTasksAndIssues()
      ]);
      
      console.log('Ended issues response:', endedIssuesResponse);
      console.log('Not ended issues response:', notEndedIssuesResponse);
      
      // Extract issues from the combined response
      let endedIssues = [];
      let notEndedIssues = [];
      
      // Process ended tasks and issues response
      if (endedIssuesResponse && endedIssuesResponse.data && Array.isArray(endedIssuesResponse.data)) {
        endedIssues = taskApi.filterIssues(endedIssuesResponse.data);
      } else if (endedIssuesResponse && Array.isArray(endedIssuesResponse)) {
        endedIssues = taskApi.filterIssues(endedIssuesResponse);
      } else if (endedIssuesResponse && endedIssuesResponse.tasks && Array.isArray(endedIssuesResponse.tasks)) {
        endedIssues = taskApi.filterIssues(endedIssuesResponse.tasks);
      }
      
      // Process not ended tasks and issues response
      if (notEndedIssuesResponse && notEndedIssuesResponse.data && Array.isArray(notEndedIssuesResponse.data)) {
        notEndedIssues = taskApi.filterIssues(notEndedIssuesResponse.data);
      } else if (notEndedIssuesResponse && Array.isArray(notEndedIssuesResponse)) {
        notEndedIssues = taskApi.filterIssues(notEndedIssuesResponse);
      } else if (notEndedIssuesResponse && notEndedIssuesResponse.tasks && Array.isArray(notEndedIssuesResponse.tasks)) {
        notEndedIssues = taskApi.filterIssues(notEndedIssuesResponse.tasks);
      }
      
      // Filter issues by current month
      const currentMonthEndedIssues = filterByCurrentMonth(endedIssues);
      const currentMonthNotEndedIssues = filterByCurrentMonth(notEndedIssues);
      
      // Calculate issue counts for current month
      const resolvedCount = currentMonthEndedIssues.length;
      const activeCount = currentMonthNotEndedIssues.length;
      const totalCount = resolvedCount + activeCount;
      
      setTotalIssues(totalCount);
      setActiveIssues(activeCount);
      setResolvedIssues(resolvedCount);
      
      console.log('Issues counts for current month:', {
        total: totalCount,
        active: activeCount,
        resolved: resolvedCount
      });
      
      return {
        total: totalCount,
        active: activeCount,
        resolved: resolvedCount
      };
      
    } catch (error) {
      console.error('Error fetching issues data:', error);
      return {
        total: 0,
        active: 0,
        resolved: 0
      };
    }
  };

  // Mock data for analytics - in real app, this would come from API
  const mockAnalyticsData = {
    sites: {
      title: 'Sites',
      metrics: [
        { label: 'Total no.of sites', value: sitesCount.toString() }
      ]
    },
    employees: {
      title: 'Employees',
      metrics: [
        { label: 'Total Assign employees', value: totalAssignedEmployees.toString() },
        { label: 'On Duty employees', value: onDutyEmployees.toString() },
        { label: 'Off Duty employees', value: offDutyEmployees.toString() }
      ]
    },
    rides: {
      title: 'Rides',
      metrics: [
        { label: 'On going Rides', value: ongoingRides.toString() },
        { label: 'Upcoming going Rides', value: upcomingRides.toString() },
        { label: 'Pending Rides', value: notStartedRides.toString() }
      ]
    },
    inventory: {
      title: 'Inventory(real time)',
      metrics: [
        { label: 'Total Items', value: totalInventoryItems.toString() },
        { label: 'Items In Good Condition', value: goodConditionItems.toString() },
        { label: 'Items under maintenance', value: underMaintenanceItems.toString() }
      ]
    },
    tasks: {
      title: 'Tasks(Monthly)',
      metrics: [
        { label: 'Total Assigned tasks', value: totalTasks.toString() },
        { label: 'On going Tasks', value: ongoingTasks.toString() },
        { label: 'Completed Tasks', value: completedTasks.toString() }
      ]
    },
    issues: {
      title: 'Issues(Monthly)',
      metrics: [
        { label: 'Total Assigned Issues', value: totalIssues.toString() },
        { label: 'Active Issues', value: activeIssues.toString() },
        { label: 'Resolved Issues', value: resolvedIssues.toString() }
      ],
      isHighlighted: true // Issues card has blue underline in wireframe
    }
  };

  const hydrateStateFromCache = (cached) => {
    if (!cached) return;
    setSitesCount(cached.sitesCount ?? 0);
    setTotalAssignedEmployees(cached.totalAssignedEmployees ?? 0);
    setOnDutyEmployees(cached.onDutyEmployees ?? 0);
    setOffDutyEmployees(cached.offDutyEmployees ?? 0);
    setUpcomingRides(cached.upcomingRides ?? 0);
    setOngoingRides(cached.ongoingRides ?? 0);
    setNotStartedRides(cached.notStartedRides ?? 0);
    setTotalInventoryItems(cached.totalInventoryItems ?? 0);
    setGoodConditionItems(cached.goodConditionItems ?? 0);
    setUnderMaintenanceItems(cached.underMaintenanceItems ?? 0);
    setTotalTasks(cached.totalTasks ?? 0);
    setOngoingTasks(cached.ongoingTasks ?? 0);
    setCompletedTasks(cached.completedTasks ?? 0);
    setTotalIssues(cached.totalIssues ?? 0);
    setActiveIssues(cached.activeIssues ?? 0);
    setResolvedIssues(cached.resolvedIssues ?? 0);
  };

  const loadCachedAnalytics = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const cachedRaw = localStorage.getItem(ANALYTICS_CACHE_KEY);
      if (!cachedRaw) {
        return null;
      }

      const cached = JSON.parse(cachedRaw);
      if (!cached?.timestamp || Date.now() - cached.timestamp > ANALYTICS_CACHE_TTL) {
        localStorage.removeItem(ANALYTICS_CACHE_KEY);
        return null;
      }

      return cached.data;
    } catch (cacheError) {
      console.warn('Failed to load analytics cache:', cacheError);
      return null;
    }
  };

  const persistAnalyticsCache = (data) => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(
        ANALYTICS_CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          data
        })
      );
    } catch (cacheError) {
      console.warn('Failed to persist analytics cache:', cacheError);
    }
  };

  useEffect(() => {
    if (isLoadingPermissions || !canViewEmployeeOverview) {
      return;
    }
    fetchEmployeeOverviewData();
  }, [fetchEmployeeOverviewData, isLoadingPermissions, canViewEmployeeOverview]);

  useEffect(() => {
    if (isLoadingPermissions || !canViewEmployeeAttendance) {
      return;
    }
    fetchAttendanceWeekData();
  }, [fetchAttendanceWeekData, isLoadingPermissions, canViewEmployeeAttendance]);

  useEffect(() => {
    if (isLoadingPermissions || !canViewRides) {
      return;
    }
    fetchDriverRideWeekData();
  }, [fetchDriverRideWeekData, isLoadingPermissions, canViewRides]);

  useEffect(() => {
    if (isLoadingPermissions) {
      return;
    }

    const needsCoreFetch =
      canViewSite ||
      canViewEmployeeOverview ||
      canViewEmployeeAttendance ||
      canViewRides ||
      canViewInventory ||
      canViewTasksIssues;

    if (!needsCoreFetch) {
      setIsLoading(false);
      return;
    }

    const cachedData = loadCachedAnalytics();
    if (cachedData?.analyticsData) {
      hydrateStateFromCache(cachedData);
      setAnalyticsData(cachedData.analyticsData);
      setIsLoading(false);
    }

    // Fetch real analytics data
    const fetchAnalyticsData = async () => {
      if (!cachedData) {
        setIsLoading(true);
      }
      setError(null);
      
      try {
        let sitesCount = 0;
        let assignedCount = 0;
        let onDutyCount = 0;
        let offDutyCount = 0;
        let rideStatuses = { upcoming: 0, ongoing: 0, notStarted: 0 };
        let inventoryData = { total: 0, goodCondition: 0, underMaintenance: 0 };
        let taskData = { total: 0, ongoing: 0, completed: 0 };
        let issuesData = { total: 0, active: 0, resolved: 0 };

        const needsEmployeeMetrics =
          canViewEmployeeOverview || canViewEmployeeAttendance;

        if (canViewSite) {
          const sitesResponse = await getAllSites();
          console.log('Sites API response:', sitesResponse);

          if (sitesResponse && Array.isArray(sitesResponse)) {
            sitesCount = sitesResponse.length;
          } else if (sitesResponse && sitesResponse.data && Array.isArray(sitesResponse.data)) {
            sitesCount = sitesResponse.data.length;
          } else if (sitesResponse && sitesResponse.sites && Array.isArray(sitesResponse.sites)) {
            sitesCount = sitesResponse.sites.length;
          }

          setSitesCount(sitesCount);
        }

        if (needsEmployeeMetrics) {
          const validUsersResponse = await dashboardApi.getValidUsersCount();
          console.log('Valid users API response:', validUsersResponse);

          if (validUsersResponse && validUsersResponse.total_scheduled) {
            assignedCount = validUsersResponse.total_scheduled;
          } else if (validUsersResponse && typeof validUsersResponse === 'number') {
            assignedCount = validUsersResponse;
          }

          setTotalAssignedEmployees(assignedCount);

          const dutyDataResponse = await dashboardApi.getAllUsersWithDutyData();
          console.log('Duty data API response:', dutyDataResponse);

          if (dutyDataResponse && Array.isArray(dutyDataResponse)) {
            onDutyCount = dutyDataResponse.filter(
              user => user.scheduled_now === true && user.on_duty === true
            ).length;
            offDutyCount = dutyDataResponse.filter(
              user => user.scheduled_now === true && user.off_duty === true
            ).length;
          } else if (dutyDataResponse && dutyDataResponse.users && Array.isArray(dutyDataResponse.users)) {
            onDutyCount = dutyDataResponse.users.filter(
              user => user.scheduled_now === true && user.on_duty === true
            ).length;
            offDutyCount = dutyDataResponse.users.filter(
              user => user.scheduled_now === true && user.off_duty === true
            ).length;
          }

          setOnDutyEmployees(onDutyCount);
          setOffDutyEmployees(offDutyCount);
        }

        if (canViewRides) {
          console.log('Fetching ride statuses...');
          rideStatuses = await getRideStatuses();
          console.log('Ride statuses:', rideStatuses);

          setUpcomingRides(rideStatuses.upcoming);
          setOngoingRides(rideStatuses.ongoing);
          setNotStartedRides(rideStatuses.notStarted);
        }

        if (canViewInventory) {
          console.log('Fetching inventory data...');
          inventoryData = await fetchInventoryData();
          console.log('Inventory data:', inventoryData);
        }

        if (canViewTasksIssues) {
          console.log('Fetching task data...');
          taskData = await fetchTaskData();
          console.log('Task data:', taskData);

          console.log('Fetching issues data...');
          issuesData = await fetchIssuesData();
          console.log('Issues data:', issuesData);
        }
        
        // Set analytics data with real counts
        const updatedAnalyticsData = {
          ...mockAnalyticsData,
          sites: {
            title: 'Sites',
            metrics: [
              { label: 'Total no.of sites', value: sitesCount.toString() }
            ]
          },
          employees: {
            title: 'Employees',
            metrics: [
              { label: 'Total Assign employees', value: assignedCount.toString() },
              { label: 'On Duty employees', value: onDutyCount.toString() },
              { label: 'Off Duty employees', value: offDutyCount.toString() }
            ]
          },
          rides: {
            title: 'Rides(Date)',
            metrics: [
              { label: 'On going Rides', value: rideStatuses.ongoing.toString() },
              { label: 'Upcoming going Rides', value: rideStatuses.upcoming.toString() },
              { label: 'Pending Rides', value: rideStatuses.notStarted.toString() }
            ]
          },
          inventory: {
            title: 'Inventory(real time)',
            metrics: [
              { label: 'Total Items', value: inventoryData.total.toString() },
              { label: 'Items In Good Condition', value: inventoryData.goodCondition.toString() },
              { label: 'Items under maintenance', value: inventoryData.underMaintenance.toString() }
            ]
          },
          tasks: {
            title: 'Tasks(Monthly)',
            metrics: [
              { label: 'Total Assigned tasks', value: taskData.total.toString() },
              { label: 'On going Tasks', value: taskData.ongoing.toString() },
              { label: 'Completed Tasks', value: taskData.completed.toString() }
            ]
          },
          issues: {
            title: 'Issues(Monthly)',
            metrics: [
              { label: 'Total Assigned Issues', value: issuesData.total.toString() },
              { label: 'Active Issues', value: issuesData.active.toString() },
              { label: 'Resolved Issues', value: issuesData.resolved.toString() }
            ],
            isHighlighted: true // Issues card has blue underline in wireframe
          }
        };

        setAnalyticsData(updatedAnalyticsData);

        persistAnalyticsCache({
          analyticsData: updatedAnalyticsData,
          sitesCount,
          totalAssignedEmployees: assignedCount,
          onDutyEmployees: onDutyCount,
          offDutyEmployees: offDutyCount,
          upcomingRides: rideStatuses.upcoming,
          ongoingRides: rideStatuses.ongoing,
          notStartedRides: rideStatuses.notStarted,
          totalInventoryItems: inventoryData.total,
          goodConditionItems: inventoryData.goodCondition,
          underMaintenanceItems: inventoryData.underMaintenance,
          totalTasks: taskData.total,
          ongoingTasks: taskData.ongoing,
          completedTasks: taskData.completed,
          totalIssues: issuesData.total,
          activeIssues: issuesData.active,
          resolvedIssues: issuesData.resolved
        });
        
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to fetch analytics data');
        // Fallback to mock data with 0 counts
        setAnalyticsData({
          ...mockAnalyticsData,
          sites: {
            title: 'Sites',
            metrics: [
              { label: 'Total no.of sites', value: '0' }
            ]
          },
          employees: {
            title: 'Employees',
            metrics: [
              { label: 'Total Assign employees', value: '0' },
              { label: 'On Duty employees', value: '0' },
              { label: 'Off Duty employees', value: '0' }
            ]
          },
          rides: {
            title: 'Rides(Date)',
            metrics: [
              { label: 'On going Rides', value: '0' },
              { label: 'Upcoming going Rides', value: '0' },
              { label: 'Pending Rides', value: '0' }
            ]
          },
          inventory: {
            title: 'Inventory(real time)',
            metrics: [
              { label: 'Total Items', value: '0' },
              { label: 'Items In Good Condition', value: '0' },
              { label: 'Items under maintenance', value: '0' }
            ]
          },
          tasks: {
            title: 'Tasks(Monthly)',
            metrics: [
              { label: 'Total Assigned tasks', value: '0' },
              { label: 'On going Tasks', value: '0' },
              { label: 'Completed Tasks', value: '0' }
            ]
          },
          issues: {
            title: 'Issues(Monthly)',
            metrics: [
              { label: 'Total Assigned Issues', value: '0' },
              { label: 'Active Issues', value: '0' },
              { label: 'Resolved Issues', value: '0' }
            ],
            isHighlighted: true // Issues card has blue underline in wireframe
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [
    isLoadingPermissions,
    canViewSite,
    canViewEmployeeOverview,
    canViewEmployeeAttendance,
    canViewRides,
    canViewInventory,
    canViewTasksIssues
  ]);

  const renderSiteOverviewCard = () => (
    <div className="relative rounded-2xl overflow-hidden min-h-[260px] shadow-lg analytics-card-enter">
      <img
        src={siteOverviewImage}
        alt="Site locations overview"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-white/75 to-transparent" />
      <div className="relative z-10 p-6 flex flex-col gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-gray-700 font-semibold">
            Site Overview
          </p>
          <h3 className={`${typography.h2} text-gray-900`}>
            You currently manage
          </h3>
        </div>
        <div>
          <p className="text-4xl font-semibold text-gray-900">
            {sitesCount}
            <span className="text-2xl font-normal text-gray-700 ml-1">
              active sites
            </span>
          </p>
        </div>
      </div>
    </div>
  );

  const renderEmployeeOverviewCard = () => (
    <Card.Gradient className="analytics-card p-4 h-full flex flex-col gap-6 analytics-card-enter">
      <div className="flex items-center justify-between">
        <h3 className={`${typography.h3} text-gray-900`}>
          Employee Overview
        </h3>
      </div>
      <div className="flex flex-col gap-6 items-center">
        <div className="relative w-full lg:w-[220px] h-[220px]">
          <Doughnut data={employeeOverviewChartData} options={employeeOverviewChartOptions} />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="text-3xl font-semibold text-gray-900">
                {employeeOverviewTotal}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Total Employees
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 w-full">
          {isEmployeeOverviewLoading ? (
            <div className="col-span-2 text-center text-gray-500 text-sm">
              Loading employee data...
            </div>
          ) : employeeOverviewSegments.length === 0 ? (
            <div className="col-span-2 text-center text-gray-500 text-sm">
              No employee data available.
            </div>
          ) : (
            employeeOverviewSegments.map(segment => (
              <div key={segment.label} className="flex items-center gap-3">
                <span
                  className="w-4 h-4 rounded-sm"
                  style={{ backgroundColor: segment.color }}
                />
                <div>
                  <p className={`${typography.p} text-gray-900 text-sm`}>
                    {segment.label}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card.Gradient>
  );

  const renderEmployeeAttendanceCard = (featured = false) => (
    <Card.Gradient
      className={`analytics-card p-4 flex flex-col gap-4 analytics-card-enter${
        featured ? ' analytics-card--featured' : ''
      }`}
    >
      <div>
        <h3 className={`${typography.h3} text-gray-900 mb-1`}>
          Employee Attendance
        </h3>
        <p className={`${typography.p} text-gray-500`}>
          Live duty distribution
        </p>
      </div>
      <div className="h-64 w-full">
        <Doughnut data={employeeAttendanceDonutData} options={employeeAttendanceDonutOptions} />
      </div>
      <div className="space-y-3">
        {[
          {
            label: 'Total Assigned employees',
            value: totalAssignedEmployees,
            badgeClass: 'bg-slate-100 text-slate-700'
          },
          {
            label: 'Employees On Duty',
            value: onDutyEmployees,
            badgeClass: 'bg-emerald-100 text-emerald-700'
          },
          {
            label: 'Employees Off Duty',
            value: offDutyEmployees,
            badgeClass: 'bg-orange-100 text-orange-700'
          }
        ].map(stat => (
          <div
            key={stat.label}
            className="flex items-center justify-between rounded-2xl px-4 py-3 bg-slate-50"
          >
            <span className="text-sm font-medium text-gray-600">
              {stat.label}
            </span>
            <span className={`text-base font-semibold px-3 py-1 rounded-xl ${stat.badgeClass}`}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>
      <div className="h-64 w-full relative">
        {isAttendanceWeekLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-500 text-sm">
              Loading attendance data...
            </div>
          </div>
        ) : (
          <Line data={employeeAttendanceChartData} options={employeeAttendanceChartOptions} />
        )}
      </div>
    </Card.Gradient>
  );

  const renderRidesCard = (featured = false) => (
    <Card.Gradient
      className={`analytics-card p-4 flex flex-col gap-5 analytics-card-enter${
        featured ? ' analytics-card--featured' : ''
      }`}
    >
      <div>
        <h3 className={`${typography.h3} text-gray-900 mb-1`}>
          Smart Navigation
        </h3>
        <p className={`${typography.p} text-gray-500`}>
          Real-time ride tracking overview
        </p>
      </div>
      <div className="rounded-2xl overflow-hidden bg-slate-100">
        <img
          src={navigationImage}
          alt="Smart navigation illustration"
          className="w-full h-[120px] object-cover"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-gray-600">Total Rides</span>
          <span className="text-lg font-semibold text-gray-900">
            {ongoingRides + upcomingRides + notStartedRides}
          </span>
        </div>
        <div className="h-32 w-full relative">
          <Bar data={rideStatusStackedBarData} options={rideStatusStackedBarOptions} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(() => {
          const total = ongoingRides + upcomingRides + notStartedRides;
          const ongoingPercentage = total > 0 ? ((ongoingRides / total) * 100).toFixed(1) : 0;
          const upcomingPercentage = total > 0 ? ((upcomingRides / total) * 100).toFixed(1) : 0;
          const pendingPercentage = total > 0 ? ((notStartedRides / total) * 100).toFixed(1) : 0;

          return [
            {
              label: 'On going Rides',
              value: ongoingRides,
              percentage: ongoingPercentage,
              color: '#1D4ED8'
            },
            {
              label: 'Upcoming rides',
              value: upcomingRides,
              percentage: upcomingPercentage,
              color: '#22C55E'
            },
            {
              label: 'Pending Rides',
              value: notStartedRides,
              percentage: pendingPercentage,
              color: '#F97316'
            }
          ].map(stat => (
            <div
              key={stat.label}
              className="rounded-2xl border border-white/60 bg-white/40 px-4 py-3"
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stat.color }}
                />
                <p className="text-sm font-medium text-gray-700">
                  {stat.label}
                </p>
              </div>
              <div className="text-left">
                <p className="text-lg font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          ));
        })()}
      </div>
      <div className="h-64 w-full relative">
        {isDriverRideWeekLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-500 text-sm">
              Loading ride data...
            </div>
          </div>
        ) : (
          <Line data={smartNavigationChartData} options={smartNavigationChartOptions} />
        )}
      </div>
    </Card.Gradient>
  );

  const renderInventoryCard = () => (
    <Card.Gradient className="analytics-card p-6 flex flex-col gap-5 analytics-card-enter">
      <div>
        <h3 className={`${typography.h3} text-gray-900 mb-1`}>
          Inventory Condition
        </h3>
        <p className={`${typography.p} text-gray-500`}>
          Pizza-style pie view of inventory states
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <div className="w-full max-h-[240px] min-h-[200px] flex items-center justify-center">
          <Pie data={inventoryPieChartData} options={inventoryPieChartOptions} />
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Total Items</span>
            <span className="text-base font-semibold text-gray-900">
              {totalInventoryItems}
            </span>
          </div>
          <div className="space-y-2">
            {hasInventoryData ? (
              inventoryPieSegments.map(segment => (
                <div
                  key={segment.key}
                  className="flex items-center justify-between rounded-2xl border border-white/60 bg-white/40 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: segment.color }}
                    />
                    <p className="text-sm font-medium text-gray-700">
                      {segment.label}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {segment.value}
                    </p>
                    <p className="text-xs text-gray-500">
                      {segment.percentage?.toFixed(1) ?? 0}%
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-white/60 bg-white/30 px-3 py-2 text-sm text-gray-500">
                Inventory metrics will appear once data is available.
              </div>
            )}
          </div>
        </div>
      </div>
    </Card.Gradient>
  );

  const renderTasksIssuesCard = () => (
    <Card.Gradient className="analytics-card p-6 flex flex-col gap-5 analytics-card-enter">
      <div>
        <h3 className={`${typography.h3} text-gray-900 mb-1`}>
          Tasks vs issues
        </h3>
        <p className={`${typography.p} text-gray-500`}>
          Comparing task throughput versus issue load
        </p>
      </div>
      <div className="flex-1 w-full min-h-[260px]">
        <Bar data={tasksVsIssuesChartData} options={tasksVsIssuesChartOptions} />
      </div>
      <div className="grid grid-cols-1 gap-3">
        {tasksVsIssuesSummaryRows.map(row => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-2xl border border-white/60 bg-white/40 px-4 py-3"
          >
            <span className="text-sm font-medium text-gray-700">
              {row.label}
            </span>
            <div className="flex items-center gap-4 text-sm font-semibold">
              <span className="inline-flex items-center gap-2 text-emerald-600">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                {row.taskValue}
              </span>
              <span className="inline-flex items-center gap-2 text-orange-500">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-400" />
                {row.issueValue}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card.Gradient>
  );

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="analytics-dashboard-header mb-6 md:mb-8">
        <div>
          <h1 className={`${typography.h1} text-white mb-2`}>
            Analytics Dashboard
          </h1>
          <p className={`${typography.p} text-black-300`}>
            Real-time insights and metrics overview
            {/* {isTestMode && (
              <span className="block text-amber-200 text-sm mt-1 font-medium">
                Preview mode — card visibility is overridden for testing
              </span>
            )} */}
          </p>
        </div>
        {/* {!isLoadingPermissions && (
          <AnalyticsPermissionTester
            isOpen={isTestPanelOpen}
            isTestMode={isTestMode}
            testVisibleCards={testVisibleCards}
            onTogglePanel={handleOpenTestPanel}
            onCardToggle={handleToggleTestCard}
            onSelectAll={handleSelectAllTestCards}
            onClearAll={handleClearAllTestCards}
            onResetToRole={handleResetTestCardsToRole}
          />
        )} */}
      </div>

      {isLoadingPermissions ? (
        <div className="analytics-permissions-loading" role="status" aria-live="polite">
          <span
            className="h-8 w-8 border-2 border-white/40 border-t-white rounded-full animate-spin"
            aria-hidden="true"
          />
          <p className={`${typography.p} text-gray-700 mt-4`}>
            Loading your analytics view...
          </p>
        </div>
      ) : layout.visibleCardCount === 0 ? (
        <div className="analytics-empty-state">
          <h2 className={`${typography.h3} text-gray-900 mb-2`}>
            {/* {isTestMode ? 'No cards selected' : 'No analytics cards available'} */}
            No analytics cards available
          </h2>
          <p className={`${typography.p} text-gray-600 max-w-md`}>
            {/* {isTestMode
              ? 'Open Testing (top right) and check one or more card permissions to preview the layout.'
              : 'You can open the Analytics dashboard, but none of the insight cards are assigned to your role yet. Contact your administrator to request access.'} */}
            You can open the Analytics dashboard, but none of the insight cards are assigned to your role yet. Contact your administrator to request access.
          </p>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p>Failed to load analytics data. Showing the latest available counts.</p>
            </div>
          )}

          {layout.showRow1 && (
            <div className="mb-6">
              <div className={layout.snapshotGridClass}>
                {layout.useStackedPairFeaturedLayout ? (
                  <>
                    <div className="analytics-snapshot-grid__stack">
                      {canViewSite && renderSiteOverviewCard()}
                      {canViewEmployeeOverview && renderEmployeeOverviewCard()}
                    </div>
                    <div className="analytics-snapshot-grid__featured">
                      {canViewEmployeeAttendance && renderEmployeeAttendanceCard(true)}
                      {canViewRides && renderRidesCard(true)}
                    </div>
                  </>
                ) : layout.stackSiteAndOverview ? (
                  <>
                    <div className="analytics-snapshot-grid__stack">
                      {canViewSite && renderSiteOverviewCard()}
                      {canViewEmployeeOverview && renderEmployeeOverviewCard()}
                    </div>
                    {canViewEmployeeAttendance && renderEmployeeAttendanceCard()}
                    {canViewRides && renderRidesCard()}
                  </>
                ) : (
                  <>
                    {canViewSite && renderSiteOverviewCard()}
                    {canViewEmployeeOverview && renderEmployeeOverviewCard()}
                    {canViewEmployeeAttendance && renderEmployeeAttendanceCard()}
                    {canViewRides && renderRidesCard()}
                  </>
                )}
              </div>
            </div>
          )}

          {layout.showRow2 && (
            <div className={`${layout.row2GridClass} mt-6 mb-6`}>
              {canViewInventory && renderInventoryCard()}
              {canViewTasksIssues && renderTasksIssuesCard()}
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default AnalyticsDashboard;
